// src/components/Industries.js
import React, { useRef, useEffect, useState } from 'react';
import ShapeDivider from './ShapeDivider';
import glassIcon from '../images/glass.svg';
import cowIcon from '../images/cow.svg';
import cookingLidIcon from '../images/cooking-lid.svg';
import wholeCakeIcon from '../images/whole-cake.svg';
import iceCreamIcon from '../images/ice-cream.svg';
import poultryIcon from '../images/poultry.svg'; // You might need to add this icon

const Industries = () => {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const industriesRef = useRef([]);
  const descriptionRef = useRef(null);

  const [content, setContent] = useState({
    title: 'Industries We Serve',
    description: 'At Three Leaf, we go beyond supply — we provide tailored ingredient solutions that add measurable value across sectors.',
    items: [
      "Beverages & Juices",
      "Dairy & Nutrition", 
      "Bakery & Confectionery",
      "Meat & Poultry",
      "Ice Cream & Desserts",
      "Foodservice & Hospitality"
    ]
  });

  const API_BASE = process.env.REACT_APP_API_BASE || (typeof window !== 'undefined' ? window.location.origin : '');

  // Map industries to their icons
  const industryIcons = {
    "Beverages & Juices": glassIcon,
    "Dairy & Nutrition": cowIcon,
    "Foodservice & Hospitality": cookingLidIcon,
    "Bakery & Confectionery": wholeCakeIcon,
    "Ice Cream & Desserts": iceCreamIcon,
    "Meat & Poultry": poultryIcon || cookingLidIcon // Fallback icon if poultry icon doesn't exist
  };

  useEffect(() => {
    // Fetch content from API
    fetch(API_BASE + '/backend/api.php/content?slug=industries')
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        if (data && data.item) {
          const jsonData = data.item.json || {};
          const jsonItems = Array.isArray(data.item.json)
            ? data.item.json
            : (Array.isArray(jsonData.items) ? jsonData.items : []);

          // Convert to simple array if items have title/description structure
          const simpleItems = jsonItems.map(item => 
            typeof item === 'string' ? item : item.title || item
          );

          setContent(prev => ({
            ...prev,
            title: data.item.title || prev.title,
            description: (jsonData.description || data.item.body || prev.description),
            items: simpleItems.length > 0 ? simpleItems : prev.items
          }));
        }
      })
      .catch(() => {});
  }, [API_BASE]);

  // Scroll animation observer
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animated');
        }
      });
    }, { threshold: 0.1 });

    if (sectionRef.current) observer.observe(sectionRef.current);
    if (headingRef.current) observer.observe(headingRef.current);
    if (descriptionRef.current) observer.observe(descriptionRef.current);
    
    industriesRef.current.forEach(el => {
      if (el) observer.observe(el);
    });

    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
      if (headingRef.current) observer.unobserve(headingRef.current);
      if (descriptionRef.current) observer.unobserve(descriptionRef.current);
      
      industriesRef.current.forEach(el => {
        if (el) observer.unobserve(el);
      });
    };
  }, []);

  // Split items into two columns for side-by-side layout
  const leftColumnItems = content.items.slice(0, 3);
  const rightColumnItems = content.items.slice(3);

  return (
    <section id="industries" className="section industries-section" ref={sectionRef}>
      <div className="container">
        <h2 className="section-title animate-on-scroll slide-in-left" ref={headingRef}>
          {content.title}
        </h2>
        
        {content.description && (
          <p className="industries-description animate-on-scroll fade-in delay-1" ref={descriptionRef}>
            {content.description}
          </p>
        )}
        
        <div className="industries-grid-container" style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '2rem',
          maxWidth: '1000px',
          margin: '0 auto'
        }}>
          {/* Left Column */}
          <div className="industries-column">
            {leftColumnItems.map((industry, index) => {
              const iconSrc = industryIcons[industry] || glassIcon;
              return (
                <div
                  key={index}
                  className={`industry-item animate-on-scroll slide-in-left delay-${index % 3}`}
                  ref={el => industriesRef.current[index] = el}
                >
                  <div className="industry-icon">
                    <img src={iconSrc} alt={`${industry} icon`} width="24" height="24" />
                  </div>
                  <span>{industry}</span>
                </div>
              );
            })}
          </div>
          
          {/* Right Column */}
          <div className="industries-column">
            {rightColumnItems.map((industry, index) => {
              const iconSrc = industryIcons[industry] || glassIcon;
              const globalIndex = index + 3; // Continue indexing from left column
              return (
                <div
                  key={globalIndex}
                  className={`industry-item animate-on-scroll slide-in-right delay-${globalIndex % 3}`}
                  ref={el => industriesRef.current[globalIndex] = el}
                >
                  <div className="industry-icon">
                    <img src={iconSrc} alt={`${industry} icon`} width="24" height="24" />
                  </div>
                  <span>{industry}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <ShapeDivider />
      
      <style>{`
        .industries-grid-container {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
          max-width: 1000px;
          margin: 0 auto;
        }
        
        .industries-column {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }
        
        .industry-item {
          display: flex;
          align-items: center;
          margin-bottom: 0;
          padding: 1.5rem;
          background-color: #ffffff;
          border-radius: 12px;
          transition: transform 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease;
          box-shadow: 0 4px 15px rgba(0,0,0,0.08);
          border: 1px solid rgba(122, 201, 67, 0.1);
        }
        
        .industry-item:hover {
          transform: translateX(10px);
          background-color: rgba(122,201,67,0.05);
          box-shadow: 0 8px 25px rgba(0,0,0,0.12);
        }
        
        .industry-icon {
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #7AC943, #5FA32E);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 1.5rem;
          flex-shrink: 0;
        }
        
        .industry-icon img {
          width: 24px;
          height: 24px;
          filter: brightness(0) invert(1);
        }
        
        .industry-item span {
          font-family: 'Quicksand', sans-serif;
          font-weight: 600;
          font-size: 1.1rem;
          color: #2d5016;
        }
        
        /* Responsive styles */
        @media (max-width: 768px) {
          .industries-grid-container {
            grid-template-columns: 1fr;
            gap: 1rem;
          }
          
          .industries-column {
            gap: 1rem;
          }
          
          .industry-item {
            padding: 1.25rem;
          }
          
          .industry-icon {
            width: 45px;
            height: 45px;
            margin-right: 1rem;
          }
          
          .industry-item span {
            font-size: 1rem;
          }
        }
        
        @media (max-width: 480px) {
          .industry-item {
            padding: 1rem;
          }
          
          .industry-icon {
            width: 40px;
            height: 40px;
          }
          
          .industry-icon img {
            width: 20px;
            height: 20px;
          }
        }
        
        /* Animation classes */
        .animated {
          opacity: 1;
          transform: translateY(0);
        }
        
        .industry-item {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.6s ease, transform 0.6s ease;
        }
        
        .slide-in-left {
          transform: translateX(-30px);
        }
        
        .slide-in-right {
          transform: translateX(30px);
        }
      `}</style>
    </section>
  );
};

export default Industries;